import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid } from '@mui/material'

import Button from '../components/Button'
import GridCard from '../components/GridCard'
import GeneralDocs from '../components/GeneralDocs'
import GeneralInfo from '../components/GeneralInfo'
import DialogRisultato from '../components/DialogRisultato'
import DialogSpostamento from '../components/DialogSpostamento'
import DialogCambioIndirizzo from '../components/DialogCambioIndirizzo'
import DialogRichiestaArbitraggio from '../components/DialogRichiestaArbitraggio'
import SelectionBar from '../components/selection/SelectionBar'
import { config } from '../settings/configs'
import {
  titlesIscrizioni as titles,
  phrasesIscrizioni as phrases,
} from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useCreateArbitraggioMutation,
  useGetCalendariQuery,
  useUpdateCalendarioMutation,
  useUpdateCalendarioIndirizzoMutation,
  useGetCampionatiQuery,
  useGetDistinteStatsQuery,
  useGetIscrizioniQuery,
  useGetSpostamentiStatsBySocietaQuery,
  useCreateSpostamentoMutation,
  useGetTesseratiStatsQuery,
} from '../hooks/api-slice'
import { setDistinta } from '../container/distinte'
import { setIscrizione } from '../container/iscrizioni'

const DashboardSocieta = () => {
  const { codiceUtente } = useSelector(state => state.home.infoUtente)
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const { data: iscrizioni } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, codiceSocieta: codiceUtente },
    { skip: !(selectedCampionato && codiceUtente) },
  )
  const { data: availableGare } = useGetCalendariQuery(
    { selectedCampionato, codiceUtente, tipologia: 'risultato' },
    { skip: !(selectedCampionato && codiceUtente) },
  )

  const selectedCampionatoIscrizioniAperte = useSelector(
    state => state.selection.selectedCampionatoIscrizioniAperte,
  )
  const selectedCampionatoCalendariPubblicati = useSelector(
    state => state.selection.selectedCampionatoCalendariPubblicati,
  )
  const selectedCampionatoChiuso = useSelector(
    state => state.selection.selectedCampionatoChiuso,
  )

  const [createArbitraggioMutation] = useCreateArbitraggioMutation()
  const [updateCalendarioMutation] = useUpdateCalendarioMutation()
  const [updateCalendarioIndirizzoMutation] =
    useUpdateCalendarioIndirizzoMutation()
  const [createSpostamentoMutation] = useCreateSpostamentoMutation()

  const dispatch = useDispatch()

  document.title = `${config?.name} - Home`

  // state to manage dialogs
  const [openIndirizzo, setOpenIndirizzo] = useState()
  const [openSpostamento, setOpenSpostamento] = useState()
  const [openRisultato, setOpenRisultato] = useState()
  const [openArbitraggio, setOpenArbitraggio] = useState()

  const handleClickOpenIndirizzo = () => setOpenIndirizzo(true)
  const handleClickOpenSpostamento = () => setOpenSpostamento(true)
  const handleClickOpenRisultato = () => setOpenRisultato(true)
  const handleClickOpenArbitraggio = () => setOpenArbitraggio(true)

  const handleCloseIndirizzo = () => setOpenIndirizzo()
  const handleCloseSpostamento = () => setOpenSpostamento()
  const handleCloseRisultato = () => setOpenRisultato()
  const handleCloseArbitraggio = () => setOpenArbitraggio()

  const { setFeedback } = useFeedback()

  const { data: spostamentiStats } = useGetSpostamentiStatsBySocietaQuery(
    { selectedCampionato, codiceUtente },
    { skip: !(selectedCampionato && codiceUtente) },
  )

  const { data: campionati } = useGetCampionatiQuery()
  const stagione = campionati?.find(
    c => c.codice === selectedCampionato,
  )?.stagione
  const { data: tesseratiStats } = useGetTesseratiStatsQuery(
    { stagione, codiceUtente },
    { skip: !(stagione && codiceUtente) },
  )

  const dArgs = { campionato: selectedCampionato, codiceUtente }
  const dSkip = { skip: !(selectedCampionato && codiceUtente) }
  const { data: distinteStats } = useGetDistinteStatsQuery(dArgs, dSkip)

  const updateIndirizzo = async (selectedGare, presso, indirizzo, citta) => {
    if (!selectedGare?.length) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    for (const idGara of selectedGare) {
      const arg = { id: idGara, presso, indirizzo, citta }
      const result = await updateCalendarioIndirizzoMutation(arg)
      if (result?.error) {
        setFeedback(result?.error?.data?.message)
        return
      }
    }

    handleCloseIndirizzo()
    setFeedback(
      phrases.phrasesIndirizzoSuccess,
      false,
      titles.titleIndirizzo,
      'calendari',
    )
  }

  const performSpostamento = async spostamento => {
    if (!spostamento?.selectedGara) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    const body = {
      codiceUtente,
      idGara: spostamento?.selectedGara,
      tipologia: spostamento?.selectedFunction,
      nuovaData: spostamento?.newData,
      nuovaOra: spostamento?.newOra,
      checkPayment: spostamento?.checkPayment,
    }
    const result = await createSpostamentoMutation(body)
    if (!result?.error) {
      handleCloseSpostamento()
      setFeedback(
        phrases.phraseSpostamentoDomandaSuccess,
        false,
        titles.titleSpostamentoDomanda,
        'spostamenti',
      )
    }
  }

  const updateRisultato = async (id, risultato) => {
    if (!id) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    const arg = { id, tipologia: 'risultato', ...risultato }
    const result = await updateCalendarioMutation(arg)

    if (!result?.error) {
      handleCloseRisultato()
      setFeedback(
        phrases.phraseRisultatoSuccess,
        false,
        titles.titleRisultato,
        'calendari',
      )
    }
  }

  const addArbitraggio = async (selectedGare, checkPayment) => {
    if (!selectedGare?.length) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    for (let idGara of selectedGare) {
      const body = { idGara, checkPayment: !!checkPayment }
      const result = await createArbitraggioMutation(body)
      if (result?.error) {
        setFeedback(result?.error?.data?.message)
        return
      }
    }

    handleCloseArbitraggio()
    setFeedback(
      phrases.phraseArbitraggioSuccess,
      false,
      titles.titleArbitraggio,
      'arbitraggi',
    )
  }

  const GridButton = ({ text, to, onClick }) => (
    <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
      <center>
        <Button {...(to ? { to } : { onClick })} children={text} />
      </center>
    </Grid>
  )

  return (
    <Grid
      container
      paddingX={2}
      marginY={2}
      rowSpacing={3}
      columnSpacing={2}
      alignItems='stretch'
      sx={{ width: '100%' }}
    >
      <GeneralInfo />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar yCampionato />
      </Grid>
      {!selectedCampionatoChiuso && (
        <Grid container spacing={2} alignItems='center' sx={{ width: '100%' }}>
          {selectedCampionatoIscrizioniAperte &&
            config?.features?.iscrizioni && (
              <GridButton
                text='Nuova iscrizione'
                to='/iscrizione'
                onClick={() => dispatch(setIscrizione())}
              />
            )}
          {selectedCampionatoCalendariPubblicati &&
            config?.features?.calendari && (
              <GridButton
                text='Cambio indirizzo'
                onClick={handleClickOpenIndirizzo}
              />
            )}
          {selectedCampionatoCalendariPubblicati &&
            config?.features?.spostamenti && (
              <GridButton
                text='Richiesta spostamento'
                onClick={handleClickOpenSpostamento}
              />
            )}
          {selectedCampionatoCalendariPubblicati &&
            config?.features?.calendari && (
              <GridButton
                text='Invio risultato'
                onClick={handleClickOpenRisultato}
              />
            )}
          {selectedCampionatoCalendariPubblicati &&
            config?.features?.arbitraggi && (
              <GridButton
                text='Richiesta arbitraggio'
                onClick={handleClickOpenArbitraggio}
              />
            )}
          {selectedCampionatoCalendariPubblicati &&
            config?.features?.distinte && (
              <GridButton
                text='Nuova distinta'
                to='/distinta'
                onClick={() => dispatch(setDistinta())}
              />
            )}
        </Grid>
      )}
      {config?.features?.iscrizioni && (
        <GridCard
          title='Iscrizioni'
          subtitle1={`${
            iscrizioni?.filter(i => i.status === 'complete')?.length || 0
          } ${
            iscrizioni?.filter(i => i.status === 'complete')?.length === 1
              ? 'completa'
              : 'complete'
          }`}
          subtitle2={`${
            iscrizioni?.filter(i => i.status === 'incomplete')?.length || 0
          } ${
            iscrizioni?.filter(i => i.status === 'incomplete')?.length === 1
              ? 'da completare'
              : 'da completare'
          }`}
          linkTo='/iscrizioni'
          isError={
            iscrizioni?.filter(i => i.status === 'incomplete')?.length > 0
          }
        />
      )}
      {config?.features?.spostamenti && (
        <GridCard
          title='Spostamenti'
          subtitle1={`${spostamentiStats?.wRisposta || 0} da confermare`}
          subtitle2={`${spostamentiStats?.wDomanda || 0} in attesa`}
          linkTo='/spostamenti'
          isError={spostamentiStats?.wRisposta > 0}
        />
      )}
      {config?.features?.calendari && (
        <GridCard
          title='Risultati'
          subtitle1={`${availableGare?.length || 0} da inviare`}
          isError={availableGare?.length > 0}
        />
      )}
      {config?.features?.tesserati && (
        <GridCard
          title='Tesserati'
          subtitle1={`${tesseratiStats?.players || 0} ${
            tesseratiStats?.players === 1 ? 'atleta' : 'atleti'
          }`}
          subtitle2={`${tesseratiStats?.staff || 0} ${
            tesseratiStats?.staff === 1 ? 'staff' : 'staff'
          }`}
          linkTo='/tesserati'
        />
      )}
      {config?.features?.distinte && (
        <GridCard
          title='Distinte'
          subtitle1={`${distinteStats?.all || 0} ${
            distinteStats?.all === 1 ? 'distinta' : 'distinte'
          }`}
          linkTo='/distinte'
        />
      )}
      <GeneralDocs />
      <DialogCambioIndirizzo
        open={openIndirizzo}
        handleClose={handleCloseIndirizzo}
        triggerFunction={updateIndirizzo}
      />
      <DialogSpostamento
        open={openSpostamento}
        handleClose={handleCloseSpostamento}
        triggerFunction={performSpostamento}
      />
      <DialogRisultato
        open={openRisultato}
        handleClose={handleCloseRisultato}
        triggerFunction={updateRisultato}
      />
      <DialogRichiestaArbitraggio
        open={openArbitraggio}
        handleClose={handleCloseArbitraggio}
        triggerFunction={addArbitraggio}
      />
    </Grid>
  )
}

export default DashboardSocieta
